<!-- Component can be used in microscope and periscope -->
<template>
  <ol class="step-track">
    <li
      v-for="(step, index) in steps"
      :key="`${index}-${step.label}`"
      class="step"
      :data-state="step.state"
      :data-ending="step.ending"
      :aria-current="step.state === 'current' ? 'step' : undefined"
    >
      <span class="step-marker" aria-hidden="true"></span>
      <span class="step-label">{{ step.label }}</span>
    </li>
  </ol>
</template>

<script setup lang="ts">
import type { TrackStep } from './AppStepTrack.types'

// The steps of a case in order, each marked by where the case is: passed, at, skipped (greyed out, kept
// in place) or to come. A finished last step takes the colour of how the case ended.
defineProps<{
  steps: TrackStep[]
}>()
</script>

<style lang="scss" scoped>
.step-track {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-4) var(--space-8);
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: var(--font-size-xs);
  line-height: var(--font-size-xs-line-height);
}

.step {
  display: inline-flex;
  align-items: center;
  gap: var(--space-6);
  color: var(--color-fg-default);

  // The connector between two steps, drawn before every step but the first.
  & + .step::before {
    content: '';
    width: var(--space-16);
    height: 1px;
    background-color: var(--color-border-default);
  }

  &[data-state='current'] {
    font-weight: var(--font-weight-semibold);
  }

  &[data-state='skipped'],
  &[data-state='upcoming'] {
    color: var(--color-fg-subtle);
  }

  &[data-state='skipped'] .step-label {
    text-decoration: line-through;
  }
}

.step-marker {
  width: 0.75em;
  height: 0.75em;
  flex: none;
  border: 1px solid var(--color-border-default);
  border-radius: 50%;

  [data-state='done'] > & {
    border-color: var(--color-brand-default);
    background-color: var(--color-brand-default);
  }

  [data-state='current'] > & {
    border: 2px solid var(--color-warning-emphasis);
    background-color: var(--color-warning-subtle);
  }

  [data-ending='approved'] > & {
    border-color: var(--color-success-emphasis);
    background-color: var(--color-success-emphasis);
  }

  [data-ending='rejected'] > & {
    border-color: var(--color-danger-emphasis);
    background-color: var(--color-danger-emphasis);
  }

  [data-ending='neutral'] > & {
    border-color: var(--color-fg-subtle);
    background-color: var(--color-fg-subtle);
  }
}
</style>
